/**
 * Drives per-video-frame callbacks using requestVideoFrameCallback when available
 * (fires once per decoded camera frame, no duplicate inference), falling back to rAF.
 */
export interface FrameLoopTick {
  /** ms timestamp to pass to the tracker (monotonic). */
  timestamp: number
  /** True if this is a new camera frame (always true with rVFC). */
  newFrame: boolean
}

type VideoWithRVFC = HTMLVideoElement & {
  requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number; presentedFrames: number }) => void) => number
  cancelVideoFrameCallback?: (handle: number) => void
}

export class FrameLoop {
  private handle = 0
  private running = false
  private lastTime = -1
  private useRVFC: boolean

  constructor(private video: HTMLVideoElement, private onFrame: (tick: FrameLoopTick) => void, forceRAF = false) {
    this.useRVFC = !forceRAF && typeof (video as VideoWithRVFC).requestVideoFrameCallback === 'function'
  }

  get mode(): 'rvfc' | 'raf' {
    return this.useRVFC ? 'rvfc' : 'raf'
  }

  start(): void {
    if (this.running) return
    this.running = true
    this.schedule()
  }

  stop(): void {
    this.running = false
    const v = this.video as VideoWithRVFC
    if (this.useRVFC && v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(this.handle)
    else cancelAnimationFrame(this.handle)
  }

  private schedule(): void {
    const v = this.video as VideoWithRVFC
    if (this.useRVFC && v.requestVideoFrameCallback) {
      this.handle = v.requestVideoFrameCallback((now) => {
        if (!this.running) return
        this.onFrame({ timestamp: now, newFrame: true })
        this.schedule()
      })
    } else {
      this.handle = requestAnimationFrame((now) => {
        if (!this.running) return
        const t = this.video.currentTime
        const newFrame = t !== this.lastTime
        this.lastTime = t
        this.onFrame({ timestamp: now, newFrame })
        this.schedule()
      })
    }
  }
}
